import { useState, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useUser } from "../hooks/useAuthCompat";
import { Sparkles, Send } from "lucide-react";
import { API } from "../hooks/useApi";
import { readCsrfCookie } from "../context/AuthContext";

export default function Guide() {
  const { isSignedIn } = useUser();
  const [params] = useSearchParams();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);
  const firedPrefill = useRef(false);

  const send = async (text) => {
    if (!text.trim() || loading) return;
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    setLoading(true);
    try {
      const resp = await fetch(`${API}/ai/guide/chat?message=${encodeURIComponent(text)}${sessionId ? `&session_id=${sessionId}` : ""}`, {
        method: "POST",
        credentials: "include",
        headers: { "X-CSRF-Token": readCsrfCookie() || "" },
      });
      if (!resp.ok || !resp.body) {
        setMessages((m) => [...m, { role: "assistant", content: "Something went wrong reaching Mr.E. Please try again." }]);
        setLoading(false);
        return;
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let assistantText = "";
      let sources = [];
      setMessages((m) => [...m, { role: "assistant", content: "", sources: [] }]);
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const payload = JSON.parse(part.slice(6));
          if (payload.type === "sources") sources = payload.sources;
          if (payload.type === "delta") assistantText += payload.content;
          if (payload.type === "error") assistantText += `\n\n(Mr.E hit an error: ${payload.content})`;
          if (payload.type === "done" && payload.session_id) setSessionId(payload.session_id);
          setMessages((m) => {
            const copy = [...m];
            copy[copy.length - 1] = { role: "assistant", content: assistantText, sources };
            return copy;
          });
        }
      }
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Something went wrong reaching Mr.E. Please try again." }]);
    }
    setLoading(false);
  };

  const renderMarkdown = (text) => text
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\n/g, "<br/>");

  useEffect(() => {
    const q = params.get("q");
    if (q && !firedPrefill.current && isSignedIn !== undefined) {
      firedPrefill.current = true;
      if (isSignedIn) send(q);
      else setInput(q);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, isSignedIn]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  if (!isSignedIn) {
    return <div className="max-w-xl mx-auto px-6 py-24 text-center text-white/60" data-testid="guide-signed-out">Sign in to talk with Mr.E.</div>;
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-10 flex flex-col h-[calc(100vh-4rem)]" data-testid="guide-page">
      <div className="flex items-center gap-2 mb-6">
        <Sparkles className="w-5 h-5 text-brandAccent" />
        <h1 className="text-xl font-display">ASK MR.E</h1>
      </div>
      <div className="flex-1 overflow-y-auto space-y-5 pb-4">
        {messages.length === 0 && (
          <p className="text-white/70 text-sm" data-testid="guide-empty-state">
            Ask about an episode, book, lesson, or your next educational step. Answers are grounded only in content you can access.
          </p>
        )}
        {messages.map((m, i) => (
          <div key={i} data-testid={`guide-message-${m.role}-${i}`} className={m.role === "user" ? "text-right" : ""}>
            <div className={`inline-block max-w-[80%] px-4 py-3 text-sm ${m.role === "user" ? "bg-white text-black" : "bg-surface border border-border"}`}>
              {m.role === "assistant" && m.content ? (
                <span dangerouslySetInnerHTML={{ __html: renderMarkdown(m.content) }} />
              ) : (
                m.content || (loading && i === messages.length - 1 ? "…" : "")
              )}
            </div>
            {m.sources?.length > 0 && (
              <p className="text-[11px] text-white/60 mt-1">Sources: {m.sources.map((s) => s.title).join(", ")}</p>
            )}
          </div>
        ))}
        <div ref={bottomRef} />
      </div>
      <form onSubmit={(e) => { e.preventDefault(); send(input); }} className="flex gap-2 pt-4 border-t border-white/10">
        <input
          data-testid="guide-chat-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask Mr.E…"
          className="flex-1 bg-white/5 border border-white/15 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-white/30"
        />
        <button data-testid="guide-chat-send" type="submit" disabled={loading} className="bg-white text-black px-4 py-3 disabled:opacity-40">
          <Send className="w-4 h-4" />
        </button>
      </form>
      <p className="text-[10px] text-white/55 mt-3">Educational only — not medical, legal, or financial advice.</p>
    </div>
  );
}
